import type { MouseEvent } from 'react'
import type { FindingListItem } from '../../lib/api.ts'
import { listedFindingRoute } from '../../lib/findingsList.ts'
import { FINDING_STATE_LABEL } from '../../lib/findingsReport.ts'
import { toPath } from '../../lib/route.ts'
import { checkSides } from '../../lib/verificationItems.ts'
import { TD, TH } from '../DataTable.tsx'
import { FOCUS_RING } from '../focusRing.ts'

const HEADINGS = ['Alterado em', 'Conta', 'Conferência', 'Situação', 'O que foi apontado']

/** A miss has no state: it was never a finding of ours. */
const stateOf = (item: FindingListItem) =>
  item.state === null ? 'Não sinalizamos' : FINDING_STATE_LABEL[item.state]

/**
 * One page of the findings behind a number (#556).
 *
 * What was flagged is read from the evidence, as the acompanhamento reads it:
 * the two sides the check compared, not a re-reading of the documents.
 */
export function FindingsListTable({
  items,
  onOpen,
}: {
  items: readonly FindingListItem[]
  /** Called as a conferência's link is followed; see `FeedbackCard`. */
  onOpen: (item: FindingListItem, event: MouseEvent<HTMLAnchorElement>) => void
}) {
  return (
    <div className="w-full overflow-x-auto">
      <table className="w-full border-collapse">
        <thead>
          <tr>
            {HEADINGS.map((heading) => (
              <th key={heading} scope="col" className={TH}>
                {heading}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {items.map((item) => {
            const sides =
              item.check === null
                ? undefined
                : checkSides({ check: item.check, evidence: item.evidence } as never)
            return (
              <tr key={item.id}>
                <td className={`${TD} whitespace-nowrap tabular-nums`}>
                  {new Date(item.at).toLocaleString('pt-BR')}
                </td>
                <td className={TD}>{item.accountName}</td>
                <td className={`${TD} whitespace-nowrap`}>
                  <a
                    href={toPath(listedFindingRoute(item))}
                    target="_blank"
                    rel="noreferrer"
                    className={`text-text-brand rounded-base font-semibold ${FOCUS_RING}`}
                    onClick={(event) => onOpen(item, event)}
                  >
                    Filial {item.branchCode} · Orçamento {item.quotationNumber}
                  </a>
                </td>
                <td className={`${TD} whitespace-nowrap`}>{stateOf(item)}</td>
                <td className={TD}>
                  <div className="flex flex-col gap-1">
                    {item.message !== null && <span>{item.message}</span>}
                    {sides !== undefined &&
                      (sides.prescribed !== undefined || sides.quoted !== undefined) && (
                        <span className="text-tSmall text-text-weak">
                          Receita:{' '}
                          <span className="text-text-strong">{sides.prescribed ?? '—'}</span> ·
                          Orçamento: <span className="text-text-strong">{sides.quoted ?? '—'}</span>
                        </span>
                      )}
                    {item.note !== null && item.note.trim() !== '' && (
                      <blockquote className="border-stroke-weak text-tSmall border-l-2 pl-3">
                        {item.note}
                      </blockquote>
                    )}
                  </div>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
